// Kode Pairing. Concept lines (JEV ui.region_gate, all kept):
//   code_header   Job: name the screen and how a code is used. Message: make a
//                 code, the sender texts the bare code to the bot. Action: none.
//                 Container: plain spacing.
//   code_form     Job: create a code for a named sender. Message: a label and
//                 a validity produce one 6-digit code. Action: Buat Kode.
//                 Container: card, recipe core.form_card; beside the list
//                 from 1024 (a form sits beside the records it creates).
//   code_created  Job: show the new code so the operator reads it out.
//                 Message: the code, its label, its expiry. Action: Salin
//                 kode. Container: a hairline readout panel at the top of the
//                 list column (JEV ui.component_recipe mono_readout_panel):
//                 the code in mono at 48 to 57px grouped "482 913", Salin
//                 kode confirms "Tersalin" in words; one 8px rise on enter.
//   code_list     Job: see codes by status and delete an unused one.
//                 Message: code in mono, status, label, expiry or use.
//                 Action: Hapus. Container: rows, recipe core.table from 1024
//                 (one header row, column rules), stacked rows below; a new
//                 code enters with one quiet rise (motion tier 1).
// Hand-written inside the kit Section: the kit has no form or record list
// composition.
import { useEffect, useId, useState, type FormEvent } from "react";
import { Section } from "@bot-whatsapp/ui";
import { ApiError, isUnauthorized, useDashboardApi } from "./api";
import { useDelayed, useOnUnauthorized, useResource } from "./data";
import { formatCode, formatDateTime } from "./format";
import type { PairingCode, PairingCodeFilter } from "./types";
import {
  CodeChip,
  ConfirmDialog,
  CopyCodeButton,
  EmptyState,
  FilterChips,
  LoadError,
  Notice,
  RowsSkeleton,
  ScreenHeader,
  StatusBadge,
  type ChipOption,
  type ConfirmRequest,
} from "./ui";
import { CheckGlyph } from "@bot-whatsapp/ui/src/kit/glyphs";

const FILTERS: ChipOption<PairingCodeFilter>[] = [
  { value: "active", label: "Aktif" },
  { value: "used", label: "Terpakai" },
  { value: "expired", label: "Kedaluwarsa" },
  { value: "all", label: "Semua" },
];

const STATUS: Record<PairingCode["status"], { label: string; tone: "success" | "info" | "neutral" }> = {
  active: { label: "Aktif", tone: "success" },
  used: { label: "Terpakai", tone: "info" },
  expired: { label: "Kedaluwarsa", tone: "neutral" },
};

const EMPTY: Record<PairingCodeFilter, { title: string; body: string }> = {
  active: { title: "Tidak ada kode aktif", body: "Buat kode baru dengan formulir di halaman ini, lalu bacakan kodenya ke pengirim." },
  used: { title: "Belum ada kode terpakai", body: "Kode pindah ke sini setelah pengirim mengirim kode itu ke nomor bot." },
  expired: { title: "Tidak ada kode kedaluwarsa", body: "Kode yang dihapus akan tampil di sini." },
  all: { title: "Belum ada kode pairing", body: "Buat kode pertama dengan formulir di halaman ini." },
};

function codeMeta(c: PairingCode): string {
  if (c.status === "used" && c.usedAt) return `Dipakai ${formatDateTime(c.usedAt)} oleh ${c.usedByPhoneMasked ?? "nomor tidak diketahui"}`;
  if (c.status === "expired") return c.expiresAt ? `Dihapus ${formatDateTime(c.expiresAt)}` : "Dihapus";
  return c.expiresAt ? `Berlaku sampai ${formatDateTime(c.expiresAt)}` : `Dibuat ${formatDateTime(c.createdAt)}, berlaku sampai dipakai`;
}

export function CodesScreen() {
  const api = useDashboardApi();
  const onUnauthorized = useOnUnauthorized();
  const headingId = useId();
  const formTitleId = useId();
  const listTitleId = useId();
  const readoutTitleId = useId();
  const labelId = useId();
  const labelHintId = useId();

  const [status, setStatus] = useState<PairingCodeFilter>("active");
  const [label, setLabel] = useState("");
  const [creating, setCreating] = useState(false);
  const [labelError, setLabelError] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [created, setCreated] = useState<PairingCode | null>(null);
  const [confirm, setConfirm] = useState<ConfirmRequest | null>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  const list = useResource(() => api.listCodes(status), [api, status]);
  const slow = useDelayed(list.loading && !list.data);

  async function create(e: FormEvent) {
    e.preventDefault();
    if (creating) return;
    const clean = label.trim();
    if (!clean) {
      setLabelError("Isi label agar kode mudah dikenali.");
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      const code = await api.createCode(clean);
      setCreated(code);
      setLabel("");
      setLabelError(null);
      if (status === "active" || status === "all") list.reload();
      else setStatus("active");
    } catch (err) {
      if (isUnauthorized(err)) onUnauthorized();
      setCreateError("Kode belum dibuat. Periksa koneksi ke server, lalu coba lagi.");
    } finally {
      setCreating(false);
    }
  }

  function askDelete(c: PairingCode) {
    setConfirmError(null);
    setConfirm({
      title: `Hapus kode ${c.code}?`,
      body: `Kode untuk ${c.label} langsung kedaluwarsa dan tidak bisa dipakai untuk pairing lagi.`,
      confirmLabel: "Hapus kode",
      run: async () => {
        await api.deleteCode(c.id);
        if (created?.id === c.id) setCreated(null);
      },
    });
  }

  async function runConfirm() {
    if (!confirm || confirmBusy) return;
    setConfirmBusy(true);
    setConfirmError(null);
    try {
      await confirm.run();
      setConfirm(null);
      list.reload();
    } catch (err) {
      if (isUnauthorized(err)) onUnauthorized();
      setConfirmError(
        err instanceof ApiError && err.status === 409
          ? "Kode ini sudah dipakai, jadi tidak bisa dihapus."
          : "Kode belum dihapus. Coba lagi sebentar lagi.",
      );
    } finally {
      setConfirmBusy(false);
    }
  }

  const items = list.data?.items ?? [];

  return (
    <Section id="kode" className="dash-screen" composition="custom" variant="form-beside-rows" labelledBy={headingId}>
      <ScreenHeader
        id={headingId}
        title="Kode Pairing"
        meta="Buat kode, lalu minta pengirim mengirim angka kodenya ke nomor bot. Kode berlaku sampai dipakai sekali."
      />

      <div className="dash-side">
        <section className="dash-card" aria-labelledby={formTitleId}>
          <form className="dash-form" onSubmit={create} noValidate>
            <h2 id={formTitleId} className="kit-title">
              Kode baru
            </h2>
            <div className="field">
              <label htmlFor={labelId}>Label pengirim</label>
              <input
                id={labelId}
                type="text"
                value={label}
                maxLength={64}
                autoComplete="off"
                onChange={(e) => {
                  setLabel(e.target.value);
                  if (labelError) setLabelError(null);
                }}
                aria-invalid={labelError ? true : undefined}
                aria-describedby={labelHintId}
                required
              />
              <p id={labelHintId} className="field-hint" data-state={labelError ? "error" : undefined}>
                {labelError ?? `Nama posko atau petugas, ${label.length} dari 64 karakter.`}
              </p>
            </div>
            {createError ? <Notice tone="danger" title="Kode gagal dibuat">{createError}</Notice> : null}
            <button type="submit" className="btn-primary btn-block" aria-busy={creating || undefined}>
              Buat Kode
            </button>
          </form>
        </section>

      </div>

      <div className="dash-main">
      {created ? (
        <section key={created.id} className="dash-readout" aria-labelledby={readoutTitleId}>
          <div className="dash-readout-head">
            <h2 id={readoutTitleId} className="dash-readout-title">
              <CheckGlyph className="dash-readout-icon" />
              <span>Kode baru untuk {created.label}</span>
            </h2>
            <p className="kit-meta">
              {created.expiresAt ? (
                <>
                  Berlaku sampai <span className="dash-time">{formatDateTime(created.expiresAt)}</span>
                </>
              ) : (
                "Berlaku sampai dipakai sekali"
              )}
            </p>
          </div>
          <p className="dash-readout-code kit-num" aria-label={`Kode ${created.code.split("").join(" ")}`}>
            {formatCode(created.code)}
          </p>
          <div className="dash-readout-copy">
            <CopyCodeButton value={created.code} />
          </div>
          <p className="kit-meta dash-readout-note">
            Minta pengirim mengirim <CodeChip>{created.code}</CodeChip> ke nomor bot, lalu nama dan NIK seperti{" "}
            <CodeChip>Budi Santoso 3201234567890123</CodeChip>.
          </p>
        </section>
      ) : null}
      <section className="dash-region" aria-labelledby={listTitleId}>
        <div className="dash-region-head">
          <h2 id={listTitleId} className="kit-title">
            Daftar kode
          </h2>
        </div>
        <FilterChips label="Status kode" value={status} options={FILTERS} onChange={setStatus} />
        {list.error && !list.data ? (
          <LoadError what="Daftar kode" onRetry={list.reload} />
        ) : !list.data ? (
          slow ? <RowsSkeleton /> : null
        ) : items.length === 0 ? (
          <EmptyState title={EMPTY[status].title} body={EMPTY[status].body} />
        ) : (
          <>
            <div className="dash-table-box">
              <table className="dash-table" aria-describedby={listTitleId}>
                <thead>
                  <tr>
                    <th scope="col" className="dash-col-code">Kode</th>
                    <th scope="col" className="dash-col-status">Status</th>
                    <th scope="col">Untuk</th>
                    <th scope="col" className="dash-col-delete">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((c) => {
                    const st = STATUS[c.status];
                    return (
                      <tr key={c.id} data-new={created?.id === c.id || undefined}>
                        <td className="dash-nowrap">
                          <span className="kit-num dash-code-value">{formatCode(c.code)}</span>
                        </td>
                        <td>
                          <StatusBadge tone={st.tone}>{st.label}</StatusBadge>
                        </td>
                        <td>
                          <span className="dash-cell-lines">
                            <span className="dash-strong">{c.label}</span>
                            <span className="dash-cell-meta">{codeMeta(c)}</span>
                          </span>
                        </td>
                        <td className="dash-cell-action">
                          {c.status === "active" ? (
                            <button
                              type="button"
                              className="btn-secondary dash-danger-quiet"
                              aria-label={`Hapus kode ${c.code}`}
                              onClick={() => askDelete(c)}
                            >
                              Hapus
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <ul className="dash-rows dash-stack">
              {items.map((c) => {
                const st = STATUS[c.status];
                return (
                  <li key={c.id} className="dash-row dash-code" data-new={created?.id === c.id || undefined}>
                    <div className="dash-code-head">
                      <p className="dash-code-value kit-num">{formatCode(c.code)}</p>
                      <StatusBadge tone={st.tone}>{st.label}</StatusBadge>
                    </div>
                    <p className="dash-row-meta dash-code-meta">
                      <span className="dash-ink">{c.label}</span>
                      <span>{codeMeta(c)}</span>
                    </p>
                    {c.status === "active" ? (
                      <div className="dash-code-action">
                        <button
                          type="button"
                          className="btn-secondary dash-danger-quiet"
                          aria-label={`Hapus kode ${c.code}`}
                          onClick={() => askDelete(c)}
                        >
                          Hapus
                        </button>
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </section>
      </div>

      <ConfirmDialog
        request={confirm}
        busy={confirmBusy}
        error={confirmError}
        onConfirm={runConfirm}
        onClose={() => setConfirm(null)}
      />
    </Section>
  );
}
